<template>
    <div class="w-full xl:container xl:mx-auto px-4 py-8">
      <AssetsKitDump
        v-if="isDev"
        title="AssetsKit — Collection POISE"
        :loading="akLoading"
        :error="akError"
        :api-routes="akDebugRoutes"
        :media-items="akDebugMediaItems"
      >
        <div v-if="akCollectionMeta" class="mt-2">
          <p class="text-gray-400 mb-1">── Collection: {{ akCollectionMeta.name || akCollectionMeta.slug || 'Unknown collection' }} ({{ akCollectionItems.length }} products) ──</p>
          <pre>{{ JSON.stringify(akCollectionDebugPayload, null, 2) }}</pre>
        </div>
        <template v-else-if="!akLoading">
          <p class="text-yellow-400 mb-2">Collection "poise" could not be loaded — raw response:</p>
          <pre>{{ JSON.stringify(akRawCollections, null, 2) }}</pre>
        </template>
      </AssetsKitDump>
      <AssetsKitSignatureCollection
        v-if="showAssetsKitPreview"
        :collection-name="akCollectionMeta.name"
        :collection-slug="akCollectionMeta.slug"
        :collection-slogan="akCollectionMeta.slogan"
        :collection-description="akCollectionMeta.description"
        :headline="contentProductTitle"
        :editorial-hook="t('editorialHook')"
        :display-order="displayColorOrder"
        :hero-color="selectedQueryColor || ''"
        :items="akCollectionItems"
        :fallback-cards="variants"
        :locale-prefix="localePrefix"
        product-type-path="/sandals"
        :focus-price="variants[0]?.price"
      />

      <template v-else-if="showLegacyFallback">
      <section class="grid gap-10 py-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center">
        <div class="order-2 max-w-xl lg:order-1">
          <p class="text-[0.68rem] uppercase tracking-[0.32em] text-gray-400">{{ t('heroLabel') }}</p>
          <h1 class="mt-5 text-[3.6rem] font-light leading-[0.88] tracking-[-0.06em] text-black md:text-[5.5rem]">
            POISE 110
          </h1>
          <p class="mt-5 text-base italic tracking-wide text-gray-500 md:text-lg">{{ t('editorialHook') }}</p>
          <p class="mt-7 max-w-lg text-lg leading-relaxed text-gray-600">{{ t('intro') }}</p>

          <div class="mt-9 flex flex-wrap gap-3">
            <NuxtLink
              :to="leadVariant ? productLink(leadVariant.color) : productLink('black')"
              class="inline-flex items-center justify-center rounded-full bg-black px-7 py-4 text-[0.72rem] font-medium uppercase tracking-[0.26em] text-white transition-colors hover:bg-gray-900"
            >
              {{ t('heroPrimaryCta') }}
            </NuxtLink>
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-full border border-black px-7 py-4 text-[0.72rem] font-medium uppercase tracking-[0.26em] text-black transition-colors hover:bg-black hover:text-white"
              @click="scrollToNewsletter"
            >
              {{ t('heroSecondaryCta') }}
            </button>
          </div>
        </div>

        <NuxtLink
          :to="leadVariant ? productLink(leadVariant.color) : productLink('black')"
          class="order-1 block lg:order-2"
        >
          <div class="relative overflow-hidden rounded-[2rem] bg-[#f7f3ed]">
            <KImage
              v-if="leadVariant"
              :src="leadVariant.image"
              :alt="`${t('title')} ${t(`colors.${leadVariant.color}`)}`"
              class="h-full w-full object-contain"
              width="1800"
              height="2200"
              sizes="100vw lg:55vw"
              loading="eager"
              :quality="88"
            />
          </div>
        </NuxtLink>
      </section>

      <section class="mt-20 border-t border-gray-200 pt-16">
        <div class="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div class="max-w-2xl">
            <p class="text-[0.68rem] uppercase tracking-[0.32em] text-gray-400">{{ t('statementLabel') }}</p>
            <h2 class="mt-4 text-4xl font-light leading-[0.92] tracking-[-0.05em] text-black md:text-5xl">
              {{ t('statementTitle') }}
            </h2>
          </div>

          <p class="max-w-md text-sm leading-relaxed text-gray-500 md:text-right">
            {{ t('statementIntro') }}
          </p>
        </div>

        <div class="mt-12 grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4">
          <NuxtLink
            v-for="product in variants"
            :key="product.color"
            :to="productLink(product.color)"
            class="group block"
          >
            <div class="relative aspect-[4/5] overflow-hidden rounded-[1.5rem] bg-[#faf8f4]">
              <KImage
                :src="product.image"
                :alt="`${t('title')} ${t(`colors.${product.color}`)}`"
                class="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                width="1200"
                height="1500"
                sizes="100vw md:50vw lg:25vw"
                :quality="85"
              />
            </div>

            <div class="mt-5 text-left">
              <p class="text-[0.7rem] uppercase tracking-[0.26em] text-gray-500">{{ t(`colors.${product.color}`) }}</p>
              <p class="mt-3 text-base text-black">{{ product.price }} €</p>
            </div>
          </NuxtLink>
        </div>
      </section>

      <section class="mt-20 grid gap-10 border-t border-gray-200 pt-16 lg:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)] lg:items-center">
        <div class="relative overflow-hidden rounded-[2rem] bg-[#faf8f4]">
          <KImage
            v-if="leadVariant"
            :src="leadVariant.image"
            :alt="`${t('title')} ${t(`colors.${leadVariant.color}`)}`"
            class="h-full w-full object-contain"
            width="1600"
            height="1800"
            sizes="100vw lg:50vw"
            :quality="88"
          />
        </div>

        <div class="max-w-xl lg:pl-6">
          <p class="text-[0.68rem] uppercase tracking-[0.32em] text-gray-400">{{ t('focusLabel') }}</p>
          <h2 class="mt-5 text-4xl font-light leading-[0.92] tracking-[-0.05em] text-black md:text-5xl">{{ t('title') }}</h2>
          <p class="mt-4 text-base italic tracking-wide text-gray-500">{{ t('editorialHook') }}</p>
          <p class="mt-8 text-lg leading-relaxed text-gray-600">{{ t('intro') }}</p>

          <div class="mt-8 flex items-end gap-4">
            <p class="text-3xl font-light text-black">{{ leadVariant?.price || 490 }} €</p>
            <p class="pb-1 text-sm uppercase tracking-[0.2em] text-gray-400">{{ leadVariant ? t(`colors.${leadVariant.color}`) : '' }}</p>
          </div>

          <div class="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-full bg-black px-7 py-4 text-[0.72rem] font-medium uppercase tracking-[0.26em] text-white transition-colors hover:bg-gray-900"
              @click="scrollToNewsletter"
            >
              {{ t('focusPrimaryCta') }}
            </button>
            <NuxtLink
              :to="leadVariant ? productLink(leadVariant.color) : productLink('black')"
              class="inline-flex items-center justify-center rounded-full border border-black px-7 py-4 text-[0.72rem] font-medium uppercase tracking-[0.26em] text-black transition-colors hover:bg-black hover:text-white"
            >
              {{ t('focusSecondaryCta') }}
            </NuxtLink>
          </div>

          <p class="mt-6 max-w-md text-sm leading-relaxed text-gray-500">
            {{ t('focusNote') }}
          </p>
        </div>
      </section>

      <p class="mt-16 pt-8 border-t border-gray-200 text-center text-gray-600 text-sm max-w-2xl mx-auto">
        {{ $t('common.brandBlurb') }}
        <NuxtLink :to="localePrefix ? '/fr/brand' : '/brand'" class="text-black underline ml-1">{{ $t('common.brand') }}</NuxtLink>
      </p>
      </template>
      <ProductTechSpecs product="sandals" />
      <ProductSeoBand product="sandals" />
      <FrenchHeelsSeoBand />
      <EditorialCollectionCta />
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref } from 'vue'
import { useProductTypes } from '~/composables/useProductTypes'
import { fetchAssetsKitCollectionBySlug } from '~/utils/assetskitCollectionLookup'
import { buildAssetsKitProductSeoVariants, buildCollectionStructuredData, cleanProductSeoText } from '~/utils/assetskitProductSeo'
import { getStaticAssetsKitCollectionResponse } from '~/utils/assetskitStaticCollections'
import { buildProductColorPath, getProductTypeRouteSlug, normalizeColorRoute, toColorRouteSlug } from '~/utils/colorRoute'
import { getLocalePrefix } from '~/utils/locales'

const AK_PRODUCT_SPACE_ID = '052eeb97-9908-4b19-b5ba-6be2e922f2f8'
const AK_COLLECTION_SLUG = 'poise'
const SITE_URL = 'https://kendrick.paris'
const isDev = process.dev
const colorOrder = ['black', 'beige', 'red', 'yellow'] as const
type ProductColorKey = typeof colorOrder[number]
type ProductVariantCard = {
  color: ProductColorKey
  image: string
  price: number
  title: string
  description: string
}
type LocalCollectionCopy = Record<
  | 'editorialHook'
  | 'focusLabel'
  | 'focusNote'
  | 'focusPrimaryCta'
  | 'focusSecondaryCta'
  | 'heroLabel'
  | 'heroPrimaryCta'
  | 'heroSecondaryCta'
  | 'intro'
  | 'statementIntro'
  | 'statementLabel'
  | 'statementTitle'
  | 'title',
  string
> & {
  colors: Record<string, string>
}

const savedColor = ref<ProductColorKey | null>(null)

function isProductColorKey(value: string): value is ProductColorKey {
  return (colorOrder as readonly string[]).includes(value)
}

const createDefaultAkResult = () => getStaticAssetsKitCollectionResponse(AK_PRODUCT_SPACE_ID, AK_COLLECTION_SLUG) || ({
  collection: null,
  rawLookup: null,
  lookupPayloads: [],
  routes: [],
  error: null
})
const hasStaticAkFallback = Boolean(createDefaultAkResult()?.collection)
const { t: i18nT } = useI18n()
const route = useRoute()
const currentLocale = useSiteLocale()
const localePrefix = computed(() => getLocalePrefix(currentLocale.value))
const isFrench = computed(() => currentLocale.value === 'fr')
const displayColorOrder = computed<string[]>(() => [...colorOrder])

const { data: akResult, pending: akLoading, refresh: refreshAkCollection } = useAsyncData(
  `assetskit-collection-${AK_COLLECTION_SLUG}-${currentLocale.value}`,
  () => fetchAssetsKitCollectionBySlug(AK_PRODUCT_SPACE_ID, AK_COLLECTION_SLUG, currentLocale.value),
  {
    lazy: import.meta.client,
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] ?? nuxtApp.static.data[key],
    default: createDefaultAkResult,
    watch: [currentLocale]
  }
)

const akError = computed(() => akResult.value?.error || null)
const akCollection = computed(() => akResult.value?.collection || null)
const akRawCollections = computed(() => akResult.value?.rawLookup || null)
const akRoutes = computed(() => akResult.value?.routes || [])

const akCollectionMeta = computed(() => {
  if (!akCollection.value || akCollection.value.error) return null
  return akCollection.value.collection || (akCollection.value.id ? akCollection.value : null)
})

const akDebugRoutes = computed(() => {
  return akRoutes.value
})

const akCollectionItems = computed(() => {
  if (!akCollection.value || akCollection.value.error) return []
  if (Array.isArray(akCollection.value.items)) return akCollection.value.items
  if (Array.isArray(akCollection.value.products)) {
    return akCollection.value.products.map((product: any) => ({ products: product }))
  }
  return []
})

const showAssetsKitPreview = computed(() => !!akCollectionMeta.value && akCollectionItems.value.length > 0)
const showLegacyFallback = computed(() => !showAssetsKitPreview.value && !akLoading.value && !hasStaticAkFallback)

const akCollectionDebugPayload = computed(() => ({
  collection: {
    id: akCollectionMeta.value?.id,
    space_id: akCollectionMeta.value?.space_id,
    name: akCollectionMeta.value?.name,
    slug: akCollectionMeta.value?.slug,
    description: akCollectionMeta.value?.description,
    cover_image: akCollectionMeta.value?.cover_image,
    status: akCollectionMeta.value?.status,
    sort_order: akCollectionMeta.value?.sort_order,
    created_at: akCollectionMeta.value?.created_at,
    updated_at: akCollectionMeta.value?.updated_at,
    slogan: akCollectionMeta.value?.slogan
  },
  items: akCollectionItems.value.map((item: any) => ({
    id: item.products?.id,
    title: item.products?.title,
    slug: item.products?.slug,
    sku: item.products?.sku,
    price: item.products?.price_amount,
    variants: Array.isArray(item.products?.variants)
      ? item.products.variants.map((variant: any) => ({
          id: variant.id,
          title: variant.title,
          sku: variant.sku,
          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3,
          price_amount: variant.price_amount,
          inventory_quantity: variant.inventory_quantity,
          images: variant.images,
          photos: variant.photos,
          photos_zoom: variant.photos_zoom
        }))
      : []
  }))
}))

const akDebugMediaItems = computed(() => {
  const mediaMap = new Map<string, { label: string, url: string }>()

  for (const item of akCollectionItems.value) {
    const product = item?.products
    const productLabel = product?.slug || product?.title || 'product'
    const variants = Array.isArray(product?.variants) ? product.variants : []

    for (const variant of variants) {
      const variantLabel = [productLabel, variant?.option1, variant?.option2].filter(Boolean).join(' · ')
      const groups = [
        { label: `${variantLabel} · images`, urls: Array.isArray(variant?.images) ? variant.images : [] },
        { label: `${variantLabel} · photos`, urls: Array.isArray(variant?.photos) ? variant.photos : [] },
        { label: `${variantLabel} · zoom`, urls: Array.isArray(variant?.photos_zoom) ? variant.photos_zoom : [] }
      ]

      for (const group of groups) {
        for (const url of group.urls) {
          if (!url || mediaMap.has(url)) continue
          mediaMap.set(url, { label: group.label, url })
        }
      }
    }
  }

  return Array.from(mediaMap.values())
})

const selectedQueryColor = computed(() => {
  const rawColor = Array.isArray(route.params.color)
    ? route.params.color[0]
    : route.params.color || (Array.isArray(route.query.color) ? route.query.color[0] : route.query.color)
  if (typeof rawColor !== 'string') return null
  const normalizedColor = normalizeColorRoute(rawColor)
  return isProductColorKey(normalizedColor) ? normalizedColor : null
})

const productLink = (color: string) => buildProductColorPath('/sandals', color, localePrefix.value)

// Static translations
const translations: Record<'fr' | 'en', LocalCollectionCopy> = {
  fr: {
    title: 'POISE 110 Sandale',
    heroLabel: 'Le statement',
    heroPrimaryCta: 'Découvrir la silhouette',
    heroSecondaryCta: 'Demander l’accès',
    statementLabel: 'Palette',
    statementTitle: 'Une silhouette. Quatre expressions.',
    statementIntro: 'La même architecture se déploie ici en noir, carmin, ivoire et safran. Plus palette que catalogue.',
    focusLabel: 'Focus produit',
    focusPrimaryCta: 'Demander l’accès',
    focusSecondaryCta: 'Voir le modèle',
    focusNote: 'La liste privée reçoit en premier les ouvertures de production, les sorties et les mises à jour de disponibilité.',
    editorialHook: 'Construction ouverte. Posture fermée.',
    intro: 'Architecture ouverte, forme close. Le talon 110mm du POISE maintient la structure même dans sa silhouette la plus essentielle.',
    colors: {
      yellow: 'Safran',
      black: 'Noir',
      red: 'Carmin',
      beige: 'Ivoire'
    }
  },
  en: {
    title: 'POISE 110 Sandal',
    heroLabel: 'The statement',
    heroPrimaryCta: 'Discover the silhouette',
    heroSecondaryCta: 'Request access',
    statementLabel: 'Palette',
    statementTitle: 'One silhouette. Four expressions.',
    statementIntro: 'The same object, opened here in black, carmine, ivoire and saffron. More palette than catalogue.',
    focusLabel: 'Focus product',
    focusPrimaryCta: 'Request access',
    focusSecondaryCta: 'View the model',
    focusNote: 'The private list receives production openings, release access and availability updates before the public.',
    editorialHook: 'Open construction. Closed stance.',
    intro: 'Minimal heeled sandals maintaining structure and posture. Essential form, maximum presence.',
    colors: {
      yellow: 'Saffron',
      black: 'Black',
      red: 'Carmine',
      beige: 'Ivoire'
    }
  }
}

const t = (key: string): string => {
  const keys = key.split('.')
  const localeCopy = currentLocale.value === 'fr'
    ? translations.fr
    : currentLocale.value === 'en'
      ? translations.en
      : null

  if (localeCopy) {
    let value: unknown = localeCopy

    for (const k of keys) {
      value = value && typeof value === 'object' && k in value
        ? (value as Record<string, unknown>)[k]
        : undefined
    }

    if (typeof value === 'string') return value
  }

  if (key.startsWith('colors.')) return i18nT(key)
  if (key === 'editorialHook') return i18nT('editorialHook.sandals')
  if (key === 'title') return i18nT('products.sandals')
  return key
}

const contentProductTitle = computed(() => (
  cleanProductSeoText(akCollectionItems.value[0]?.products?.title) || t('title')
))

const { fetchProductTypes, getProductTypePrice } = useProductTypes()

const baseVariants = [
  {
    color: 'yellow',
    title: 'POISE 110 Sandal',
    price: 490,
    image: '/k/sandals/kendrick-sandals-yellow-1.jpg',
    description: 'Introducing the inaugural masterpiece from Kendrick: sandals featuring a striking 10-centimeter heel, crafted from the softest lamb leather.'
  },
  {
    color: 'black',
    title: 'POISE 110 Sandal',
    price: 490,
    image: '/k/sandals/kendrick-sandals-black-1.jpg',
    description: 'Introducing the inaugural masterpiece from Kendrick: sandals featuring a striking 10-centimeter heel, crafted from the softest lamb leather.'
  },
  {
    color: 'red',
    title: 'POISE 110 Sandal',
    price: 490,
    image: '/k/sandals/kendrick-sandals-red-1.jpg',
    description: 'Introducing the inaugural masterpiece from Kendrick: sandals featuring a striking 10-centimeter heel, crafted from the softest lamb leather.'
  },
  {
    color: 'beige',
    title: 'POISE 110 Sandal',
    price: 490,
    image: '/k/sandals/kendrick-sandals-beige-1.jpg',
    description: 'Introducing the inaugural masterpiece from Kendrick: sandals featuring a striking 10-centimeter heel, crafted from the softest lamb leather.'
  }
] satisfies ProductVariantCard[]

const variants = computed<ProductVariantCard[]>(() => {
  console.log('Sandals index: Computing variants...')
  
  // Get price from database
  const dbPrice = getProductTypePrice.value('sandals')
  // Use locale for product title (FR: POISE 110 Sandale, EN: POISE 110 Sandal)
  const productTitle = contentProductTitle.value
  const price = dbPrice && dbPrice > 0 ? dbPrice : 490

  // Map baseVariants to use database price and locale-aware title
  const variantsWithDbPrice = baseVariants.map(variant => ({
    ...variant,
    title: productTitle,
    price // Use database price, fallback to 490€
  }))
  const orderedByCampaign = colorOrder
    .map(color => variantsWithDbPrice.find(product => product.color === color))
    .filter((product): product is ProductVariantCard => Boolean(product))

  const activeColor = selectedQueryColor.value || savedColor.value

  console.log('Sandals index: Saved color from state:', activeColor)
  if (activeColor) {
    const savedIndex = colorOrder.indexOf(activeColor)
    const reorderedColors: ProductVariantCard[] = []

    for (let i = 0; i < colorOrder.length; i++) {
      const colorIndex = (savedIndex + i) % colorOrder.length
      const color = colorOrder[colorIndex]
      const product = variantsWithDbPrice.find(p => p.color === color)
      if (product) {
        reorderedColors.push(product)
      }
    }

    console.log('Sandals index: Reorganizing with saved color first:', activeColor)
    console.log('Sandals index: Final order:', reorderedColors.map(p => p.color))
    return reorderedColors
  }

  // Always return the campaign order when no color was selected yet.
  console.log('Sandals index: Using default order:', orderedByCampaign.map(p => p.color))
  return orderedByCampaign
})

const priceValidUntil = computed(() => new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10))
const baseCanonicalUrl = computed(() => `${SITE_URL}${localePrefix.value}/${getProductTypeRouteSlug('sandals', localePrefix.value)}`)
const selectedColorSlug = computed(() => selectedQueryColor.value ? toColorRouteSlug(selectedQueryColor.value) : null)
const canonicalUrl = computed(() => selectedColorSlug.value ? `${baseCanonicalUrl.value}/${selectedColorSlug.value}` : baseCanonicalUrl.value)
const alternateEnUrl = computed(() => selectedColorSlug.value ? `${SITE_URL}/sandals/${selectedColorSlug.value}` : `${SITE_URL}/sandals`)
const alternateFrUrl = computed(() => selectedColorSlug.value ? `${SITE_URL}/fr/sandales/${selectedColorSlug.value}` : `${SITE_URL}/fr/sandales`)
const baseSeoTitle = computed(() => (
  isFrench.value
    ? 'Sandales à talon aiguille de créateur — POISE 110 | Kendrick Paris'
    : 'POISE 110 — French Designer Sandals | Kendrick Paris'
))
const baseSeoDescription = computed(() => (
  isFrench.value
    ? 'Sandales à talon aiguille de créateur : talon 110 mm, cuir d\'agneau italien, assemblées à la main en Italie. Quatre coloris. 490 €.'
    : 'Open sandal in Italian lambskin. 110mm heel. Four colorways. Hand-assembled in Italy. €490.'
))
const baseSeoKeywords = computed(() => (
  isFrench.value
    ? 'sandale talon aiguille, sandale talon créateur, sandales talon haut, sandales noires créateur, sandales à talon cuir, Kendrick Paris'
    : 'french designer sandals, luxury heeled sandals, italian lambskin sandals, kendrick paris, poise 110'
))
const collectionSeoDescription = computed(() => cleanProductSeoText(baseSeoDescription.value) || cleanProductSeoText(akCollectionMeta.value?.description))
const seoVariants = computed(() => buildAssetsKitProductSeoVariants({
  items: akCollectionItems.value,
  fallbackCards: variants.value,
  colorOrder: displayColorOrder.value,
  baseUrl: baseCanonicalUrl.value,
  defaultTitle: contentProductTitle.value,
  defaultDescription: collectionSeoDescription.value,
  colorLabel: color => t(`colors.${color}`),
  siteUrl: SITE_URL
}))
const selectedSeoVariant = computed(() => (
  selectedQueryColor.value
    ? seoVariants.value.find(variant => variant.color === selectedQueryColor.value) || null
    : null
))
const metaTitle = computed(() => {
  if (!selectedSeoVariant.value) return baseSeoTitle.value
  if (isFrench.value) return `Sandale à talon aiguille POISE 110 — ${t(`colors.${selectedSeoVariant.value.color}`)} | Kendrick Paris`
  return `${selectedSeoVariant.value.title} | Kendrick Paris`
})
const metaDescription = computed(() => selectedSeoVariant.value?.description || collectionSeoDescription.value)
const metaImage = computed(() => selectedSeoVariant.value?.image || seoVariants.value[0]?.image || `${SITE_URL}/k/sandals/kendrick-sandals-black-1.jpg`)
const structuredData = computed(() => buildCollectionStructuredData({
  collectionId: akCollectionMeta.value?.id || AK_COLLECTION_SLUG,
  collectionName: contentProductTitle.value,
  collectionDescription: collectionSeoDescription.value,
  selectedVariant: selectedSeoVariant.value,
  variants: seoVariants.value,
  category: isFrench.value ? 'Sandales' : 'Sandals',
  priceValidUntil: priceValidUntil.value
}))

const leadVariant = computed(() => variants.value[0] || null)

function scrollToNewsletter() {
  if (typeof document === 'undefined') return

  const el = document.getElementById('newsletter')
  if (!el) return

  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  const input = el.querySelector('input[type=email]') as HTMLInputElement | null
  if (input) input.focus()
}

// Fetch product types on mount to get pricing from database
onMounted(() => {
  void refreshAkCollection()
  fetchProductTypes()

  if (selectedQueryColor.value) {
    savedColor.value = selectedQueryColor.value
    sessionStorage.setItem('selectedProductColor', selectedQueryColor.value)
    return
  }

  const storedColor = sessionStorage.getItem('selectedProductColor')
  if (storedColor && isProductColorKey(storedColor)) {
    savedColor.value = storedColor
  }
})

useShareImage('/k/sandals/kendrick-sandals-black-1.jpg', 'Kendrick Paris black leather sandals')

// Set page title and SEO
useHead(() => ({
  title: metaTitle.value,
  meta: [
    { name: 'description', content: metaDescription.value },
    { name: 'keywords', content: baseSeoKeywords.value },
    { name: 'robots', content: 'index, follow' },
    { property: 'og:title', content: metaTitle.value },
    { property: 'og:description', content: metaDescription.value },
    { property: 'og:type', content: selectedSeoVariant.value ? 'product' : 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:image', content: metaImage.value },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: metaTitle.value },
    { name: 'twitter:description', content: metaDescription.value },
    { name: 'twitter:image', content: metaImage.value }
  ],
  link: [
    { rel: 'canonical', href: canonicalUrl.value },
    { rel: 'alternate', hreflang: 'en', href: alternateEnUrl.value },
    { rel: 'alternate', hreflang: 'fr', href: alternateFrUrl.value },
    { rel: 'alternate', hreflang: 'x-default', href: alternateEnUrl.value }
  ],
  script: structuredData.value
    ? [
        {
          key: 'collection-product-structured-data',
          type: 'application/ld+json',
          innerHTML: JSON.stringify(structuredData.value)
        }
      ]
    : []
}))
</script> 

<style scoped>
/* editorial — utility-only page */
</style>
